import { isAthletePayload, type JwtPayload } from '@/lib/auth/jwt'

import { trackMetaCompleteRegistration } from './metaPixel'
import {
  type SignupContact,
  trackTikTokCompleteRegistration
} from './tiktokPixel'

export type UserRole = 'fan' | 'club' | 'athlete'
export type AuthMethod = 'email' | 'google'
export type SignupFlow = 'join' | 'modal'
export type SignUpStartTrigger = 'open' | 'switch' | 'referral_link'
export type LogoutReason = 'user' | 'session_expired'
export type WatchProgressPercent = 25 | 50 | 75 | 100
export type WatchLastStep =
  | 'selected'
  | 'play_started'
  | 'progress_50'
  | 'video_completed'

export type AnalyticsEvent =
  | { event: 'sign_up_step_2_email'; user_role: UserRole; method: AuthMethod }
  | { event: 'sign_up_step_1_role'; user_role: UserRole }
  | { event: 'sign_up_step_3_code'; user_role: UserRole }
  | {
      event: 'sign_up_complete'
      user_id: string | null
      user_role: UserRole
      method: AuthMethod
      referral_id: string | null
      /** Whose code referral_id is: fan, club, athlete, agent (from auth). */
      referral_type: string | null
      signup_flow: SignupFlow
    }
  | {
      event: 'sign_up_start'
      signup_flow: 'modal'
      trigger: SignUpStartTrigger
    }
  | { event: 'sign_up_error'; user_role: UserRole; error_type: string }
  | {
      event: 'login'
      user_id: string | null
      user_role: UserRole
      method: AuthMethod
    }
  | {
      event: 'logout'
      user_id: undefined
      user_role: undefined
      login_state: 'guest'
      logout_reason: LogoutReason
    }
  | { event: 'partner_params'; referral_id: string }
  | { event: 'watch_task_select'; task_id: string; task_name: string }
  | { event: 'watch_task_play'; task_id: string }
  | {
      event: 'watch_task_progress'
      task_id: string
      progress_percent: WatchProgressPercent
    }
  | { event: 'watch_task_complete'; task_id: string; reward_ap: number }
  | { event: 'watch_exit_prompt'; task_id: string }
  | {
      event: 'watch_exit_decision'
      task_id: string
      decision: 'continue' | 'leave'
    }
  | { event: 'watch_task_abandon'; task_id: string; last_step: WatchLastStep }
  | { event: 'share_task_select'; task_id: string; task_name: string }
  | { event: 'action_task_select'; task_id: string; task_name: string }
  | { event: 'share_task_take'; task_id: string }
  | { event: 'share_task_download'; task_id: string }
  | { event: 'share_task_copy_text'; task_id: string }
  | { event: 'share_task_submit'; task_id: string }
  | { event: 'share_task_expired'; task_id: string }

declare global {
  interface Window {
    dataLayer?: unknown[]
  }
}

/**
 * GTM loads via DeferredAnalyticsScripts after tracking consent. Events pushed
 * before that wait in the dataLayer and are replayed after `gtm.js` (see
 * loadGoogleTagManager).
 *
 * `contact` is for sign_up_complete only and never enters the dataLayer: it is
 * hashed for TikTok Advanced Matching.
 */
export const pushDataLayerEvent = (
  event: AnalyticsEvent,
  contact?: SignupContact
): void => {
  if (typeof window === 'undefined') {
    return
  }
  try {
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push(event)
  } catch {
    // best-effort
  }

  // Every signup path (email, Google callback, deferred Google signup, /join)
  // already funnels through here, so mirroring the conversion to the ad pixels
  // in this one place keeps the channels from drifting apart.
  if (event.event === 'sign_up_complete') {
    trackMetaCompleteRegistration()
    trackTikTokCompleteRegistration(event.user_id, contact)
  }
}

export const entityTypeToUserRole = (entityType?: string | null): UserRole =>
  entityType === 'CLUB' ? 'club' : entityType === 'ATHLETE' ? 'athlete' : 'fan'

export const jwtRolesToUserRole = (
  roles?: string[] | null,
  payload?: JwtPayload | null
): UserRole => {
  if (!roles?.includes('USER_ROLE_CLUB')) {
    return 'fan'
  }
  return isAthletePayload(payload) ? 'athlete' : 'club'
}
